import { useMemo, useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useSearchParams } from "react-router-dom"
import { projectsApi, documentationApi, paymentsApi, usageApi, coursesApi } from "../../../services/api"
import Button from "../../../components/ui/Button"
import Card, { CardBody } from "../../../components/ui/Card"
import { Field, Select } from "../../../components/ui/Field"
import PageHeader from "../../../components/dashboard/PageHeader"
import CongratulationsModal from "../../../components/ui/CongratulationsModal"

const GENERATION_TYPES = [
  { key: 'DOCUMENTATION', label: 'Documentation', pricingQuery: 'DOCUMENTATION' },
  { key: 'PROPOSAL', label: 'Proposal', pricingQuery: 'PROPOSAL' }
]

function idFromRef (v) {
  if (v == null || v === '') return ''
  if (typeof v === 'object' && v._id != null) return String(v._id)
  return String(v)
}

/** Visual pipeline aligned with batched generation (preliminary → chapters → appendix). */
const GENERATION_STAGES = [
  { keys: ['preliminary'], label: 'Preliminary pages', hint: 'Front matter & declarations' },
  { keys: ['abstract'], label: 'Abstract', hint: 'Structured project summary' },
  { keys: ['ch1'], label: 'Chapter one', hint: 'Introduction & problem framing' },
  { keys: ['ch2'], label: 'Chapter two', hint: 'Literature review' },
  { keys: ['ch3'], label: 'Chapter three', hint: 'Methodology' },
  { keys: ['ch4'], label: 'Chapter four', hint: 'System design & analysis' },
  { keys: ['ch5'], label: 'Chapter five', hint: 'Implementation, results & conclusions' },
  { keys: ['appendix', 'references', 'body'], label: 'Appendix & references', hint: 'Supporting material & bibliography' }
]

function stageIndexForBatchKey (batchKey) {
  if (!batchKey || batchKey === 'all') return 0
  if (batchKey === 'complete') return GENERATION_STAGES.length
  const i = GENERATION_STAGES.findIndex((s) => s.keys.includes(batchKey))
  return i === -1 ? 3 : i
}

/** Server-side generation may still be running or stopped mid-way; user can resume. */
function isGenerationIncomplete (doc) {
  if (!doc) return false
  const gs = doc.generationStatus
  return gs === 'IN_PROGRESS' || gs === 'FAILED'
}

function sortSectionsForProjectList (chapters = []) {
  const list = Array.isArray(chapters) ? [...chapters] : []
  const normal = []
  let references = null
  let appendix = null

  list.forEach((ch) => {
    const title = String(ch?.title || '')
    if (!references && /\breferences\b|\bbibliography\b/i.test(title)) {
      references = ch
      return
    }
    if (!appendix && /\bappendix\b|\bappendices\b/i.test(title)) {
      appendix = ch
      return
    }
    normal.push(ch)
  })

  normal.sort((a, b) => (a.chapterNumber || 0) - (b.chapterNumber || 0))
  if (references) normal.push(references)
  if (appendix) normal.push(appendix)
  return normal
}

const Project = ({
  page,
  setPage,
  setSelectedDoc,
  setSelectedChapter
}) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState([])
  const [docsByProjectType, setDocsByProjectType] = useState({})
  const [loading, setLoading] = useState(true)
  const [generateModal, setGenerateModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [genLoading, setGenLoading] = useState(false)
  const [genProgress, setGenProgress] = useState(null)
  const [error, setError] = useState('')
  const [paidProjectIdsByType, setPaidProjectIdsByType] = useState(() => ({
    DOCUMENTATION: new Set(),
    PROPOSAL: new Set()
  }))
  const [paymentSuccessModal, setPaymentSuccessModal] = useState(null)
  const [generationSuccessModal, setGenerationSuccessModal] = useState(null)
  const [usage, setUsage] = useState(null)
  const [generationType, setGenerationType] = useState('DOCUMENTATION')
  const [courses, setCourses] = useState([])
  const [pendingCourseId, setPendingCourseId] = useState('')
  const [savingCourse, setSavingCourse] = useState(false)

  const fetchProjects = async () => {
    try {
      const res = await projectsApi.list({})
      if (res?.data?.data?.projects) setProjects(res.data.data.projects)
    } catch {
      setProjects([])
      setError('Could not load projects right now.')
    }
  }

  const fetchDocs = async () => {
    try {
      const res = await documentationApi.list({})
      if (res?.data?.data) {
        const byProject = {}
        res.data.data.forEach(d => {
          const pid = d.project?._id || d.project
          const gt = String(d.generationType || 'DOCUMENTATION')
          if (!pid) return
          if (!byProject[pid]) byProject[pid] = {}
          if (!byProject[pid][gt]) byProject[pid][gt] = d
        })
        setDocsByProjectType(byProject)
      }
    } catch {
      setDocsByProjectType({})
    }
  }

  const fetchPaidProjects = async () => {
    try {
      const res = await paymentsApi.mine({ paymentStatus: 'successful', limit: 200 })
      const list = res?.data?.data || []
      const paid = new Set(
        list.filter((p) => p?.paymentStatus === 'successful' && p?.productType === 'DOCUMENTATION_GENERATION' && p?.project).map((p) => String(p.project?._id || p.project))
      )
      const proposalPaid = new Set(
        list.filter((p) => p?.paymentStatus === 'successful' && p?.productType === 'PROPOSAL_GENERATION' && p?.project).map((p) => String(p.project?._id || p.project))
      )
      const siwesPaid = new Set()
      setPaidProjectIdsByType({
        DOCUMENTATION: paid,
        PROPOSAL: proposalPaid
      })
    } catch {
      setPaidProjectIdsByType({
        DOCUMENTATION: new Set(),
        PROPOSAL: new Set()
      })
    }
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchProjects(),
      fetchDocs(),
      fetchPaidProjects(),
      usageApi
        .get()
        .then((r) => setUsage(r?.data?.data || null))
        .catch(() => setUsage(null))
    ])
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const paymentSuccess = searchParams.get('paymentSuccess') === '1'
    const projectId = searchParams.get('projectId')
    const paidGenerationType = (searchParams.get('generationType') || 'DOCUMENTATION').toUpperCase()
    if (!paymentSuccess || !projectId) return

    const proj = projects.find((p) => String(p._id) === String(projectId))
    if (!proj) return

    setPaymentSuccessModal({
      projectId: String(projectId),
      projectTitle: proj?.title || 'this',
      generationType: paidGenerationType
    })

    const next = new URLSearchParams(searchParams)
    next.delete('paymentSuccess')
    next.delete('generationType')
    setSearchParams(next, { replace: true })
  }, [projects, searchParams, setSearchParams])

  // Formats removed: generation uses course templates only.

  const projectsById = useMemo(() => {
    const m = new Map()
    projects.forEach((p) => m.set(String(p._id), p))
    return m
  }, [projects])

  const getDocFor = (projectId, type) => docsByProjectType?.[projectId]?.[type] || null

  const closeGenerationSuccess = () => setGenerationSuccessModal(null)

  const openGeneratedDoc = () => {
    const pid = generationSuccessModal?.projectId
    const gt = generationSuccessModal?.generationType || 'DOCUMENTATION'
    if (!pid) return
    const doc = getDocFor(pid, gt)
    if (!doc) return
    navigate(`/dashboard/project/full/${doc._id}`)
    closeGenerationSuccess()
  }

  const handleGenerateDoc = async (proj, type = 'DOCUMENTATION') => {
    setSelectedProject(proj)
    setGenerationType(type)
    setPendingCourseId(proj?.courseId ? String(proj.courseId) : '')
    setGenerateModal(true)
    setError("")
  }

  const closeGenerateModal = () => {
    setGenerateModal(false)
    setError('')
    setGenProgress(null)
  }

  const selectedDoc = selectedProject ? getDocFor(selectedProject._id, generationType) : null
  const isResuming = selectedDoc && isGenerationIncomplete(selectedDoc)
  const generationTypeLabel = generationType === 'DOCUMENTATION' ? 'documentation' : 'proposal'

  useEffect(() => {
    if (!generateModal) return
    coursesApi.list().then((res) => setCourses(res?.data?.data || [])).catch(() => setCourses([]))
  }, [generateModal])

  const projectNeedsCourse = selectedProject && !idFromRef(selectedProject.courseId)

  const saveCourseForProject = async () => {
    if (!selectedProject || !pendingCourseId) {
      setError('Select your department/course before generating.')
      return null
    }
    setSavingCourse(true)
    setError('')
    try {
      const res = await projectsApi.update(selectedProject._id, { courseId: pendingCourseId })
      const updated = res?.data?.data
      if (updated) {
        setSelectedProject(updated)
        setProjects((prev) => prev.map((p) => (String(p._id) === String(updated._id) ? updated : p)))
        return updated
      }
      setError(res?.data?.message || 'Failed to save course')
      return null
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save course')
      return null
    } finally {
      setSavingCourse(false)
    }
  }

  const doGenerate = async () => {
    if (!selectedProject) return
    let projectForGen = selectedProject
    if (projectNeedsCourse) {
      const updated = await saveCourseForProject()
      if (!updated) return
      projectForGen = updated
    }
    setGenLoading(true)
    setGenProgress({ percent: 0, batchLabel: 'Starting…', sectionTitle: '', batchKey: '', step: 0, totalSteps: 0 })
    setError('')
    try {
      const successProjectId = String(projectForGen._id)
      const successProjectTitle = projectForGen?.title || ''

      const payload = {
        projectId: projectForGen._id,
        generationType
      }
      const doc = await documentationApi.generateStream(payload, (evt) => {
        if (evt.type !== 'progress') return
        setGenProgress({
          percent: typeof evt.percent === 'number' ? evt.percent : 0,
          batchLabel: evt.batchLabel || '',
          sectionTitle: evt.sectionTitle || '',
          batchKey: evt.batchKey || '',
          step: evt.step ?? 0,
          totalSteps: evt.totalSteps ?? 0
        })
      })
      if (doc) {
        await fetchDocs()
        usageApi.get().then((r) => setUsage(r?.data?.data || null)).catch(() => setUsage(null))
        setGenerateModal(false)
        setSelectedProject(null)
        setGenProgress(null)
        setGenerationSuccessModal({
          projectId: successProjectId,
          projectTitle: successProjectTitle,
          generationType
        })
      }
    } catch (e) {
      setGenProgress(null)
      const status = e?.response?.status
      const pay = e?.response?.data
      if (status === 402 || pay?.code === 'PAYMENT_REQUIRED' || e?.code === 'PAYMENT_REQUIRED') {
        const paymentMessage = pay?.message || e.message || 'Payment is required for this project before generation.'
        setError(paymentMessage)
        window.location.href = `/pricing?projectId=${encodeURIComponent(projectForGen._id)}&generationType=${encodeURIComponent(generationType)}`
        return
      }
      setError(e?.response?.data?.message || e.message || 'Failed to generate')
    } finally {
      setGenLoading(false)
    }
  }

  const openChapter = (doc, chapter) => {
    setSelectedDoc(doc)
    setSelectedChapter(chapter)
    setPage('Chapter')
  }

  if (loading) return <div className="p-4 text-slate-600 animate-soft-pulse">Loading projects...</div>

  const usageForSelectedDoc = (() => {
    const pid = selectedProject?._id
    if (!pid || !usage?.documentation?.items?.length) return null
    return usage.documentation.items.find((it) => String(it.projectId) === String(pid)) || null
  })()

  return (
    <div>
      <CongratulationsModal
        open={!!generationSuccessModal}
        projectTitle={generationSuccessModal?.projectTitle}
        onClose={closeGenerationSuccess}
        onOpenDocument={openGeneratedDoc}
      />

      {paymentSuccessModal &&
        createPortal(
          <dialog
            open
            className="modal fixed inset-0 z-[1100] items-start justify-center pt-6 sm:pt-10"
            aria-labelledby="payment-success-title"
            aria-modal="true"
          >
            <div className="modal-box max-w-lg border border-emerald-200/70 bg-white text-slate-900 shadow-2xl">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 ring-1 ring-emerald-200/70">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h3 id="payment-success-title" className="text-lg font-bold tracking-tight">
                    Payment successful
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    Payment made for <span className="font-semibold text-slate-900">{paymentSuccessModal.projectTitle}</span> project.
                    You can now generate {paymentSuccessModal.generationType === 'PROPOSAL' ? 'proposal' : 'documentation'}.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setPaymentSuccessModal(null)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    const proj = projectsById.get(String(paymentSuccessModal.projectId))
                    setPaymentSuccessModal(null)
                    if (proj) handleGenerateDoc(proj, paymentSuccessModal.generationType || 'DOCUMENTATION')
                  }}
                >
                  Generate now
                </Button>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop" onClick={() => setPaymentSuccessModal(null)}><button>close</button></form>
          </dialog>,
          document.body
        )}

      <PageHeader
        eyebrow="Workspace"
        title="Projects"
        description="Create a project, then generate documentation or a proposal aligned to your course template."
        action={<Button onClick={() => navigate("new")}>New project</Button>}
      />

      {generateModal &&
        createPortal(
          <dialog
            open
            className="modal fixed inset-0 z-[1000] items-start justify-center pt-6 sm:pt-10"
            aria-labelledby="generate-doc-title"
            aria-modal="true"
          >
        <div className="modal-box max-w-lg overflow-visible border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/90 text-slate-900 shadow-2xl sm:max-w-xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700/90">Documentation</p>
              <h2 id="generate-doc-title" className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                {isResuming ? 'Resume project write-up' : `Generate ${generationTypeLabel}`}
              </h2>
            </div>
            <div className="flex shrink-0 items-start gap-2">
              <div className="rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 ring-1 ring-emerald-200/80">
                Template-aware
              </div>
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost text-slate-500 hover:text-slate-800"
                aria-label="Close"
                disabled={genLoading}
                onClick={closeGenerateModal}
              >
                ✕
              </button>
            </div>
          </div>
            {!isResuming ? (
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
            We generate <span className="font-medium text-slate-800">long-form, supervisor-ready</span> content for{' '}
              <span className="font-semibold text-slate-900">&quot;{selectedProject?.title}&quot;</span>, strictly following your course template prompt for this generation type. Payment applies if you have not completed this generation type for the project.
          </p>
            ) : (
            <p className="mt-3 rounded-lg border border-sky-200/80 bg-sky-50 px-3 py-2.5 text-sm text-sky-950">
              <span className="font-semibold">Next step:</span> Click <span className="font-semibold">Resume generation</span> below to continue writing{' '}
              <span className="font-semibold">&quot;{selectedProject?.title}&quot;</span>.
              {typeof selectedDoc?.generationTotalSections === 'number' ? (
                <span className="block mt-1 text-xs text-sky-900/90">
                  Progress: {(selectedDoc?.chapters || []).length} of {selectedDoc.generationTotalSections} sections saved. Completed sections stay on the server.
                </span>
              ) : (
                <span className="block mt-1 text-xs text-sky-900/90">
                  Completed sections stay on the server; we will pick up the next section.
                </span>
              )}
            </p>
            )}
          {usageForSelectedDoc ? (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">Usage for this project</p>
              <p className="mt-1">
                Edits: <span className="font-semibold">{usageForSelectedDoc.edits?.used ?? 0}</span> /{" "}
                <span className="font-semibold">{usageForSelectedDoc.edits?.allowed ?? 0}</span>{" "}
                (Remaining: <span className="font-semibold">{usageForSelectedDoc.edits?.remaining ?? 0}</span>)
              </p>
              <p className="mt-1">
                Regenerations: <span className="font-semibold">{usageForSelectedDoc.regeneration?.used ?? 0}</span> /{" "}
                <span className="font-semibold">{usageForSelectedDoc.regeneration?.allowed ?? 0}</span>{" "}
                (Remaining: <span className="font-semibold">{usageForSelectedDoc.regeneration?.remaining ?? 0}</span>)
              </p>
            </div>
          ) : null}

          {genLoading && genProgress ? (
            <div className="mt-6 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-inner shadow-slate-900/5">
              <div className="flex items-center justify-between gap-2 text-xs font-medium text-slate-500">
                <span>Overall progress</span>
                <span className="tabular-nums text-slate-700">{genProgress.percent}%</span>
              </div>
              <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200/80">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${Math.min(100, Math.max(0, genProgress.percent))}%` }}
                />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">{genProgress.batchLabel}</p>
              {genProgress.sectionTitle ? (
                <p className="mt-0.5 text-xs text-slate-600 line-clamp-2">{genProgress.sectionTitle}</p>
              ) : null}
              {genProgress.totalSteps > 0 ? (
                <p className="mt-1 text-xs text-slate-500">
                  Section {genProgress.step} of {genProgress.totalSteps}
                </p>
              ) : null}
              <div className="mt-4 max-h-36 overflow-y-auto rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Generation pipeline</p>
                <ol className="mt-2 grid gap-1.5 sm:grid-cols-2">
                  {GENERATION_STAGES.map((stage, idx) => {
                    const activeIdx = stageIndexForBatchKey(genProgress.batchKey)
                    const done = idx < activeIdx || genProgress.batchKey === 'complete'
                    const active = idx === activeIdx && genProgress.batchKey !== 'complete'
                    return (
                      <li
                        key={stage.label}
                        className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-xs ${
                          done ? 'bg-emerald-50/90 text-emerald-950' : active ? 'bg-white text-slate-900 ring-1 ring-emerald-200' : 'text-slate-500'
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                            done ? 'bg-emerald-600 text-white' : active ? 'bg-emerald-500 text-white animate-pulse' : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {done ? '✓' : idx + 1}
                        </span>
                        <span>
                          <span className="font-semibold">{stage.label}</span>
                          <span className="block font-normal text-slate-600">{stage.hint}</span>
                        </span>
                      </li>
                    )
                  })}
                </ol>
              </div>
              <p className="mt-3 text-[11px] leading-snug text-slate-500">
                Sections are produced in batches (front matter, abstract, chapters, appendix) so each part can be long, accurate, and faithful to your template.
              </p>
            </div>
          ) : null}

          {!isResuming && !genLoading ? (
            <div className="mt-4">
              <p className="text-sm font-semibold text-slate-800">Template source</p>
              <p className="text-xs text-slate-600 mt-1">
                This generator uses the template prompts configured on your course by admin.
              </p>
            </div>
          ) : null}

          {projectNeedsCourse ? (
            <div className={`mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 ${genLoading ? 'pointer-events-none opacity-50' : ''}`}>
              <p className="text-sm font-semibold text-amber-950">Course required</p>
              <p className="mt-1 text-xs text-amber-900/90">
                This project was created without a course. Pick your department/course so we can load the right template.
              </p>
              <div className="mt-3">
                <Field label="Department / Course">
                  <Select value={pendingCourseId} onChange={(e) => setPendingCourseId(e.target.value)}>
                    <option value="">Select department / course</option>
                    {courses.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="mt-3">
                <Button size="sm" variant="outline" loading={savingCourse} disabled={!pendingCourseId || savingCourse} onClick={saveCourseForProject}>
                  Save course
                </Button>
              </div>
            </div>
          ) : null}

          {error && <p className="text-error text-sm mt-2">{error}</p>}
          <div className="modal-action">
            <Button
              variant="outline"
              disabled={genLoading}
              onClick={closeGenerateModal}
            >
              Cancel
            </Button>
            <Button
              loading={genLoading || savingCourse}
              onClick={doGenerate}
              disabled={genLoading || savingCourse || (projectNeedsCourse && !pendingCourseId)}
            >
              {genLoading ? 'Generating…' : (isResuming ? 'Resume generation' : `Generate ${generationTypeLabel}`)}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop" onClick={closeGenerateModal}><button>close</button></form>
          </dialog>,
          document.body
        )}

      <div className="mt-2">
        <div className="flex flex-col gap-6">
          {projects.length === 0 && <p className="text-slate-600">Create a project first.</p>}
          {projects.map((proj, index) => {
            const paidByType = paidProjectIdsByType || {}
            return (
              <Card key={proj._id} className="p-0 hover:shadow-lift shadow-soft transition-shadow">
                <CardBody>
                <div>
                  <p className="font-semibold text-slate-900">
                    <span className="mr-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-100 px-2 text-xs font-semibold text-slate-700">
                      {index + 1}
                    </span>
                    {proj.title}
                  </p>
                </div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{proj.description}</p>
                <div className="mt-3 space-y-4">
                  {GENERATION_TYPES.map((t) => {
                    const doc = getDocFor(proj._id, t.key)
                    const isPaid = (paidByType[t.key] || new Set()).has(String(proj._id))
                    return (
                      <div key={t.key} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-800">{t.label}</p>
                          {isPaid ? <span className="badge badge-success badge-sm">Paid</span> : <span className="badge badge-ghost badge-sm">Not paid</span>}
                        </div>
                        {!doc ? (
                          <div className="mt-2">
                            <Button size="sm" onClick={() => handleGenerateDoc(proj, t.key)}>Generate {t.label}</Button>
                          </div>
                        ) : (
                          <div className="mt-2">
                            {isGenerationIncomplete(doc) ? (
                      <div className="mb-3 rounded-lg border border-amber-200/90 bg-amber-50/95 px-3 py-2.5 text-sm text-amber-950">
                        <p className="font-medium">Generation paused — your work is saved on the server.</p>
                        <p className="mt-1 text-xs text-amber-900/90">
                          If you lost connection or closed the tab, resume to continue. Completed sections:
                          {' '}{(doc.chapters || []).length}
                          {typeof doc.generationTotalSections === 'number' ? ` of ${doc.generationTotalSections}` : ''}.
                        </p>
                        {doc.generationStatus === 'FAILED' && doc.generationLastError ? (
                          <p className="mt-1 text-xs text-red-800/90 line-clamp-2" title={doc.generationLastError}>{doc.generationLastError}</p>
                        ) : null}
                        <div className="mt-2">
                          <Button size="sm" onClick={() => handleGenerateDoc(proj, t.key)}>Resume generation</Button>
                        </div>
                      </div>
                    ) : null}
                    <p className="text-sm text-slate-500">Edits: {doc.editCount}/100 · Regenerations: {doc.regenerationCount}/3</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigate(`/dashboard/project/full/${doc._id}`)
                        }}
                      >
                        Open full document
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          navigate(`/dashboard/editor?docId=${encodeURIComponent(String(doc._id))}&projectId=${encodeURIComponent(String(proj._id))}`)
                        }}
                      >
                        Edit in Word document
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">Read everything in one scrollable view (like a Word document), or open a section below to edit.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {sortSectionsForProjectList(doc.chapters || [])
                        .map(ch => (
                        <Button key={ch._id} size="sm" variant="ghost" onClick={() => openChapter(doc, ch)}>
                          {ch.title}
                        </Button>
                      ))}
                    </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                </CardBody>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Project
